import { useCallback, useRef, useState } from 'react';

import { createSSEStream } from '@frontend/api/sse';
import { HYPATIA_URL } from '@frontend/envConfig';

import type { ExecutionCompleteData, SSEError, StreamEvent, UserMessage } from '../types/streaming';

type UseStreamExecuteOptions = {
  onChunk: (event: StreamEvent) => void;
  onComplete: (data: ExecutionCompleteData) => void;
  onError: (error: SSEError) => void;
};

type ExecuteParams = {
  instanceId: string;
  agentId: string;
  parameters: Record<string, unknown>;
  messages?: UserMessage[];
  memoryId?: string;
  etag?: string;
  resetConversation?: boolean;
  regenerate?: boolean;
};

export function useStreamExecute(options: UseStreamExecuteOptions) {
  const { onChunk, onComplete, onError } = options;
  const [isStreaming, setIsStreaming] = useState(false);
  const abortControllerRef = useRef<AbortController | null>(null);

  const abort = useCallback(() => {
    abortControllerRef.current?.abort();
    abortControllerRef.current = null;
    setIsStreaming(false);
  }, []);

  const execute = useCallback(
    (params: ExecuteParams) => {
      const {
        instanceId,
        agentId,
        parameters,
        messages,
        memoryId,
        etag,
        resetConversation,
        regenerate,
      } = params;

      // Abort any existing stream
      abortControllerRef.current?.abort();

      const controller = new AbortController();
      abortControllerRef.current = controller;
      setIsStreaming(true);

      const url = `${HYPATIA_URL}/api/v1/${instanceId}/agents/specialized/${agentId}/stream-execute`;

      const body: Record<string, unknown> = { parameters };
      if (messages) body.messages = messages;
      if (memoryId) body.memory_id = memoryId;
      if (resetConversation) body.reset_conversation = true;
      if (regenerate) body.regenerate = true;

      const headers: Record<string, string> = {};
      if (etag) headers['If-Match'] = etag;

      createSSEStream({
        url,
        body,
        headers,
        signal: controller.signal,
        onEvent: (event: StreamEvent) => {
          if (event.event_type === 'execution_complete') {
            const data = event.data as unknown as ExecutionCompleteData;
            onComplete(data);
            setIsStreaming(false);
            abortControllerRef.current = null;
          } else if (event.event_type === 'error') {
            onError({
              message: (event.data.message as string) ?? 'Stream error',
              errorType: event.data.error_type as string | undefined,
              recoverable: (event.data.recoverable as boolean) ?? false,
            });
            setIsStreaming(false);
            abortControllerRef.current = null;
          } else if (event.event_type !== 'heartbeat') {
            onChunk(event);
          }
        },
        onError: err => {
          onError({
            message: err.message,
            recoverable: err.status !== 412 && err.status !== 428,
          });
          setIsStreaming(false);
          abortControllerRef.current = null;
        },
      });
    },
    [onChunk, onComplete, onError]
  );

  return { execute, abort, isStreaming };
}
